import React, { useState, useEffect } from 'react';
import {
    Box,
    Drawer,
    Typography,
    IconButton,
    Tabs,
    Tab,
    TextField,
    RadioGroup,
    Radio,
    Button,
    useTheme,
    Stack,
    FormControlLabel,
    Snackbar,
    Alert,
    InputAdornment
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { tppService, ThirdPartyProcessor, ProcessorType } from '../../services/tpp-service';
import { config } from '../../config/config';

const PROCESSOR_TYPES = [
    { value: 'PROCESSOR', label: 'Processor' },
    { value: 'FIDUCIARY', label: 'Fiduciary' }
];

interface AddProcessorPanelProps {
    open: boolean;
    onClose: () => void;
    onSave?: () => void;
}

const AddProcessorPanel: React.FC<AddProcessorPanelProps> = ({ open, onClose, onSave }) => {
    const theme = useTheme();
    const [currentTab, setCurrentTab] = useState(0);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [type, setType] = useState<ProcessorType>('PROCESSOR');
    const [websiteURL, setWebsiteURL] = useState('');
    const [contactEmail, setContactEmail] = useState('');
    const [contactPhone, setContactPhone] = useState('');
    const [dpaURL, setDpaURL] = useState('');
    const [emailError, setEmailError] = useState('');
    const [nameError, setNameError] = useState('');
    const [websiteURLError, setWebsiteURLError] = useState('');
    const [dpaURLError, setDpaURLError] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [serverError, setServerError] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            setName('');
            setDescription('');
            setType('PROCESSOR');
            setWebsiteURL('');
            setContactEmail('');
            setContactPhone('');
            setDpaURL('');
            setEmailError('');
            setNameError('');
            setWebsiteURLError('');
            setDpaURLError('');
            setPhoneError('');
            setServerError(null);
        }
    }, [open]);

    const validateName = (value: string): string | undefined => {
        if (!value?.trim()) {
            return 'Name is required';
        }
        if (value.trim().length < 3) {
            return 'Name must be at least 3 characters';
        }
        return undefined;
    };

    const validateEmail = (value: string): string | undefined => {
        if (!value?.trim()) {
            return undefined;
        }
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(value)) {
            return 'Please enter a valid email address';
        }
        return undefined;
    };

    const validateURL = (value: string, fieldName: string): string | undefined => {
        if (!value?.trim()) {
            return undefined;
        }
        try {
            new URL(value);
            return undefined;
        } catch {
            return `Please enter a valid URL (e.g., https://example.com)`;
        }
    };

    const validatePhone = (value: string): string | undefined => {
        if (!value?.trim()) {
            return undefined;
        }
        // Remove any non-digit characters
        const digitsOnly = value.replace(/\D/g, '');
        if (digitsOnly.length !== 10) {
            return 'Phone number must be 10 digits';
        }
        return undefined;
    };

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setName(value);
        const error = validateName(value);
        setNameError(error || '');
    };

    const handleNameBlur = () => {
        const error = validateName(name);
        setNameError(error || '');
    };

    const handleWebsiteURLChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setWebsiteURL(value);
        const error = validateURL(value, 'Website URL');
        setWebsiteURLError(error || '');
    };

    const handleWebsiteURLBlur = () => {
        const error = validateURL(websiteURL, 'Website URL');
        setWebsiteURLError(error || '');
    };

    const handleDpaURLChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setDpaURL(value);
        const error = validateURL(value, 'Data Processing Agreement URL');
        setDpaURLError(error || '');
    };

    const handleDpaURLBlur = () => {
        const error = validateURL(dpaURL, 'Data Processing Agreement URL');
        setDpaURLError(error || '');
    };

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setContactEmail(value);
        const error = validateEmail(value);
        setEmailError(error || '');
    };

    const handleEmailBlur = () => {
        const error = validateEmail(contactEmail);
        setEmailError(error || '');
    };

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setContactPhone(value);
        const error = validatePhone(value);
        setPhoneError(error || '');
    };

    const handlePhoneBlur = () => {
        const error = validatePhone(contactPhone);
        setPhoneError(error || '');
    };

    const handleSave = async () => {
        if (emailError || nameError || websiteURLError || dpaURLError || phoneError) return;

        try {
            setIsSaving(true);
            const processor: Omit<ThirdPartyProcessor, 'processorId' | 'createdDate' | 'createdBy' | 'modifiedDate' | 'modifiedBy'> = {
                name,
                description,
                type,
                website: websiteURL,
                contactEmail,
                contactPhone: `+91${contactPhone.replace(/\D/g, '')}`,
                dataProcessingAgreementUrl: dpaURL,
                isPublished: false
            };

            await tppService.createProcessor(processor);
            onClose();
            onSave?.();
        } catch (error: any) {
            console.error('Error creating processor:', error);
            setServerError(error.response?.data?.message || 'Failed to create processor');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: '600px',
                    bgcolor: theme.palette.background.paper,
                    borderLeft: `1px solid ${theme.palette.divider}`,
                },
            }}
        >
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Header */}
                <Box sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}>
                    <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                        Add Processor
                    </Typography>
                    <IconButton onClick={onClose} sx={{ color: theme.palette.text.primary }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Tabs */}
                <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <Tabs
                        value={currentTab}
                        onChange={(_, v) => setCurrentTab(v)}
                        sx={{
                            '& .MuiTab-root': {
                                color: theme.palette.text.secondary,
                                '&.Mui-selected': {
                                    color: theme.palette.primary.main,
                                },
                            },
                        }}
                    >
                        <Tab label="Details" />
                    </Tabs>
                </Box>

                {/* Tab Panels */}
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                    {currentTab === 0 && (
                        <Box sx={{ p: 3 }}>
                            <Stack spacing={3}>
                                <TextField 
                                    label="Name" 
                                    fullWidth 
                                    size="small" 
                                    required 
                                    value={name} 
                                    onChange={handleNameChange}
                                    onBlur={handleNameBlur}
                                    error={!!nameError}
                                    helperText={nameError}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: nameError ? theme.palette.error.main : theme.palette.divider,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: theme.palette.primary.main,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: theme.palette.primary.main,
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: theme.palette.text.secondary,
                                        },
                                        '& .MuiInputBase-input': {
                                            color: theme.palette.text.primary,
                                        },
                                        '& .MuiFormHelperText-root': {
                                            color: nameError ? theme.palette.error.main : theme.palette.text.secondary,
                                        },
                                    }}
                                />
                                <TextField 
                                    label="Description" 
                                    fullWidth 
                                    size="small" 
                                    multiline 
                                    rows={3} 
                                    value={description} 
                                    onChange={e => setDescription(e.target.value)} 
                                />
                                <Box>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Type</Typography>
                                    <RadioGroup row value={type} onChange={e => setType(e.target.value as ProcessorType)}>
                                        {PROCESSOR_TYPES.map(opt => (
                                            <FormControlLabel 
                                                key={opt.value} 
                                                value={opt.value} 
                                                control={<Radio />} 
                                                label={opt.label} 
                                            />
                                        ))}
                                    </RadioGroup>
                                </Box>
                                <TextField 
                                    label="Website URL" 
                                    fullWidth 
                                    size="small" 
                                    value={websiteURL} 
                                    onChange={handleWebsiteURLChange}
                                    onBlur={handleWebsiteURLBlur}
                                    error={!!websiteURLError}
                                    helperText={websiteURLError || "Enter a valid URL (e.g., https://example.com)"}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: websiteURLError ? theme.palette.error.main : theme.palette.divider,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: theme.palette.primary.main,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: theme.palette.primary.main,
                                            },
                                        },
                                    }}
                                />
                                <TextField 
                                    label="Contact Email ID" 
                                    fullWidth 
                                    size="small" 
                                    value={contactEmail} 
                                    onChange={handleEmailChange}
                                    onBlur={handleEmailBlur}
                                    error={!!emailError}
                                    helperText={emailError}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: emailError ? theme.palette.error.main : theme.palette.divider,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: theme.palette.primary.main,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: theme.palette.primary.main,
                                            },
                                        },
                                    }}
                                />
                                <TextField 
                                    label="Contact Phone Number" 
                                    fullWidth 
                                    size="small" 
                                    value={contactPhone} 
                                    onChange={handlePhoneChange}
                                    onBlur={handlePhoneBlur}
                                    error={!!phoneError}
                                    helperText={phoneError || "Enter 10-digit phone number"}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                +91
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: phoneError ? theme.palette.error.main : theme.palette.divider,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: theme.palette.primary.main,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: theme.palette.primary.main,
                                            },
                                        },
                                    }}
                                />
                                <TextField 
                                    label="Data Processing Agreement URL" 
                                    fullWidth 
                                    size="small" 
                                    value={dpaURL} 
                                    onChange={handleDpaURLChange}
                                    onBlur={handleDpaURLBlur}
                                    error={!!dpaURLError}
                                    helperText={dpaURLError || "Enter a valid URL (e.g., https://example.com/dpa)"}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: dpaURLError ? theme.palette.error.main : theme.palette.divider,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: theme.palette.primary.main,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: theme.palette.primary.main,
                                            },
                                        },
                                    }}
                                />
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                    <Button 
                                        variant="outlined" 
                                        color="primary" 
                                        onClick={onClose}
                                    >
                                        Cancel
                                    </Button>
                                    <Button 
                                        variant="contained" 
                                        color="primary" 
                                        disabled={!!emailError || !!nameError || !!websiteURLError || !!dpaURLError || !!phoneError || !name || isSaving}
                                        onClick={handleSave}
                                    >
                                        {isSaving ? 'Saving...' : 'Save'}
                                    </Button>
                                </Box>
                            </Stack>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Error Snackbar */}
            <Snackbar 
                open={!!serverError} 
                autoHideDuration={config.ui.snackbar.autoHideDuration}
                onClose={() => setServerError(null)}
                anchorOrigin={config.ui.snackbar.anchorOrigin}
            >
                <Alert onClose={() => setServerError(null)} severity="error" sx={{ width: '100%' }}>
                    {serverError}
                </Alert>
            </Snackbar>
        </Drawer>
    );
};

export default AddProcessorPanel; 